import { z } from 'zod';

/** Titik dinormalkan terhadap ukuran foto mockup: 0 = tepi kiri/atas, 1 = tepi kanan/bawah. */
const pointSchema = z.tuple([z.number().min(0).max(1), z.number().min(0).max(1)]);

export type MockupPoint = z.infer<typeof pointSchema>;

/** Kiri-atas, kanan-atas, kanan-bawah, kiri-bawah. */
export type MockupArea = [MockupPoint, MockupPoint, MockupPoint, MockupPoint];

/** Luas bertanda (shoelace): positif bila titik searah jarum jam pada koordinat layar. */
export function mockupAreaSize(area: MockupArea): number {
  let sum = 0;
  for (let i = 0; i < 4; i++) {
    const [x1, y1] = area[i]!;
    const [x2, y2] = area[(i + 1) % 4]!;
    sum += x1 * y2 - x2 * y1;
  }
  return sum / 2;
}

/**
 * Area tempel harus segi empat cembung searah jarum jam dan tidak terlalu kecil.
 * Segi empat terlipat membuat transformasi perspektif membalik desain.
 */
export function mockupAreaProblem(area: MockupArea): string | null {
  if (mockupAreaSize(area) < 0.0025)
    return 'Area tempel terlalu kecil atau urutan titiknya terbalik';
  for (let i = 0; i < 4; i++) {
    const [ax, ay] = area[i]!;
    const [bx, by] = area[(i + 1) % 4]!;
    const [cx, cy] = area[(i + 2) % 4]!;
    const cross = (bx - ax) * (cy - by) - (by - ay) * (cx - bx);
    if (cross <= 0) return 'Area tempel harus segi empat cembung tanpa sudut terlipat';
  }
  return null;
}

export const mockupAreaSchema = z
  .tuple([pointSchema, pointSchema, pointSchema, pointSchema])
  .superRefine((area, ctx) => {
    const problem = mockupAreaProblem(area);
    if (problem) ctx.addIssue({ code: 'custom', message: problem });
  });

export const MOCKUP_BLENDS = ['NORMAL', 'MULTIPLY'] as const;
export type MockupBlend = (typeof MOCKUP_BLENDS)[number];

/** Area bawaan saat mockup baru dibuat: persegi panjang di tengah foto. */
export const DEFAULT_MOCKUP_AREA: MockupArea = [
  [0.25, 0.25],
  [0.75, 0.25],
  [0.75, 0.75],
  [0.25, 0.75],
];

export const productMockupItemSchema = z.object({
  /** Kosong untuk mockup baru. */
  id: z.string().min(1).max(64).optional(),
  name: z.string().trim().min(1).max(80),
  mediaId: z.string().min(1).max(64),
  area: mockupAreaSchema,
  blend: z.enum(MOCKUP_BLENDS).default('MULTIPLY'),
  /** Halaman desain yang ditampilkan mockup ini: 1 = depan, 2 = belakang (KEPUTUSAN 4.6). */
  page: z.number().int().min(1).max(8).default(1),
  /**
   * Pilihan varian yang cocok untuk mockup ini, mis. {"warna":"hitam"}.
   * Kosong berarti cocok untuk semua varian.
   */
  selections: z.record(z.string().min(1).max(64), z.string().min(1).max(64)).default({}),
  isActive: z.boolean().default(true),
});

/** Menyusun ulang seluruh mockup produk sekaligus: urutan array menjadi sortOrder. */
export const productMockupsSchema = z.object({
  items: z.array(productMockupItemSchema).max(12),
});

export type ProductMockupItemInput = z.infer<typeof productMockupItemSchema>;
export type ProductMockupsInput = z.infer<typeof productMockupsSchema>;
